import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { HintMark } from "./Tooltip";

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string;
  /** Short explanation of the field and its options, shown via the label info mark. */
  tooltip?: string;
  /** Sits on the label row, after the info mark. */
  aside?: ReactNode;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, tooltip, aside, style, children, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? (label ? generatedId : undefined);
  const select = (
    <select
      ref={ref}
      id={selectId}
      {...rest}
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--color-border)",
        background: "var(--color-input)",
        color: "var(--color-text)",
        fontSize: "var(--font-size-md)",
        fontFamily: "var(--font-sans)",
        minHeight: "var(--touch-target)",
        ...style,
      }}
    >
      {children}
    </select>
  );
  return label ? (
    <div style={{ display: "grid", gap: "var(--space-1)" }}>
      <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <label htmlFor={selectId}>{label}</label>
        {tooltip ? <HintMark label={`About ${label}`} text={tooltip} /> : null}
        {aside}
      </span>
      {select}
    </div>
  ) : (
    select
  );
});
